/**
 * The docs' two drawings, as vector shapes in the page so that they follow the light and dark
 * themes: how a campaign's money and proof move between the brand, the escrow, creators, the
 * platforms and Morcast, and the phases of a campaign from its window to the claims.
 *
 * Each is one image to a screen reader, named by its label, and the text beside it says the same
 * in words. On a narrow screen a drawing keeps a readable size and scrolls sideways inside its
 * frame.
 */

import { glass } from "../../components/marketing.js";

const name = "fill-zinc-950 dark:fill-white";
const quiet = "fill-zinc-600 dark:fill-zinc-400";
const box =
  "fill-white stroke-zinc-900/15 dark:fill-white/[0.03] dark:stroke-white/15 [stroke-width:1.25]";
/** The one box a drawing puts forward: the brand's blue. */
const main =
  "fill-brand-500/10 stroke-brand-600 dark:fill-brand-400/10 dark:stroke-brand-400 [stroke-width:2]";
const line = "fill-none stroke-zinc-400 dark:stroke-zinc-500 [stroke-width:1.25]";
const head = "fill-zinc-400 dark:fill-zinc-500";
const caption = "mt-4 text-sm leading-6 text-zinc-600 dark:text-zinc-400";

/** The head of an arrow, for a drawing's lines to end in. Each drawing names its own. */
function ArrowHead({ id }: { id: string }) {
  return (
    <defs>
      <marker
        id={id}
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path d="M0 0L10 5L0 10z" className={head} />
      </marker>
    </defs>
  );
}

/**
 * How a campaign runs, as five numbered moves. The brand locks the budget in the escrow. Creators
 * post on X or YouTube. Morcast reads the organic reach there, publishes the result and settles.
 * The escrow then pays creators 80% and Morcast 20% of what the brand owes, and returns the rest.
 */
export function CampaignFlow() {
  const arrow = "url(#docs-flow-arrow)";
  return (
    <figure className={`p-4 sm:p-6 ${glass}`}>
      <div className="overflow-x-auto">
        <svg
          viewBox="0 64 760 300"
          role="img"
          aria-label="The brand locks a budget in the escrow on Base. Creators post on X and YouTube. Morcast reads the organic reach, publishes the result and settles. The escrow pays creators 80%, Morcast 20% and returns the rest to the brand."
          className="h-auto w-full min-w-[40rem]"
          fontSize="13"
        >
          <ArrowHead id="docs-flow-arrow" />
          <g className={line}>
            <path d="M212 112H286" markerEnd={arrow} />
            <path d="M286 136H212" markerEnd={arrow} />
            <path d="M474 124H548" markerEnd={arrow} />
            <path d="M642 152V288" markerEnd={arrow} />
            <path d="M548 316H474" markerEnd={arrow} />
            <path d="M356 280V160" markerEnd={arrow} />
            <path d="M404 160V280" markerEnd={arrow} />
          </g>
          <g textAnchor="middle">
            <rect x="24" y="96" width="188" height="56" rx="8" className={box} />
            <text x="118" y="120" fontWeight="600" className={name}>
              Brand
            </text>
            <text x="118" y="136" fontSize="11.5" className={quiet}>
              sets a budget and a target
            </text>

            <rect x="286" y="88" width="188" height="72" rx="8" className={main} />
            <text x="380" y="112" fontWeight="600" className={name}>
              Escrow on Base
            </text>
            <text x="380" y="128" fontSize="11.5" className={quiet}>
              holds the budget
            </text>
            <text x="380" y="144" fontSize="11.5" className={quiet}>
              pays by a fixed formula
            </text>

            <rect x="548" y="96" width="188" height="56" rx="8" className={box} />
            <text x="642" y="120" fontWeight="600" className={name}>
              Creators
            </text>
            <text x="642" y="136" fontSize="11.5" className={quiet}>
              post and earn by reach
            </text>

            <rect x="286" y="280" width="188" height="72" rx="8" className={box} />
            <text x="380" y="304" fontWeight="600" className={name}>
              Morcast
            </text>
            <text x="380" y="320" fontSize="11.5" className={quiet}>
              checks each post
            </text>
            <text x="380" y="336" fontSize="11.5" className={quiet}>
              publishes the result
            </text>

            <rect x="548" y="288" width="188" height="56" rx="8" className={box} />
            <text x="642" y="312" fontWeight="600" className={name}>
              X and YouTube
            </text>
            <text x="642" y="328" fontSize="11.5" className={quiet}>
              measure real reach
            </text>
          </g>
          <g fontSize="11.5" className={quiet}>
            <text x="249" y="80" textAnchor="middle">
              1 locks the budget
            </text>
            <text x="511" y="80" textAnchor="middle">
              5 creators get 80%
            </text>
            <text x="249" y="176" textAnchor="middle">
              5 the rest returns
            </text>
            <text x="632" y="224" textAnchor="end">
              2 post content
            </text>
            <text x="511" y="272" textAnchor="middle">
              3 reads organic reach
            </text>
            <text x="346" y="214" textAnchor="end">
              4 publishes the result
            </text>
            <text x="346" y="230" textAnchor="end">
              and settles
            </text>
            <text x="414" y="222">
              5 Morcast gets 20%
            </text>
          </g>
        </svg>
      </div>
      <figcaption className={caption}>
        The budget waits in the escrow and pays only for verified reach. Creators and Morcast share
        what the brand pays, 80% and 20%, and the rest of the budget returns to the brand.
      </figcaption>
    </figure>
  );
}

/** The phases of a campaign, each with when it runs and what happens in it. Not drawn to scale. */
const phases: { x: number; title: string; when: string; what: string; put?: boolean }[] = [
  { x: 24, title: "Campaign", when: "start to end", what: "creators post" },
  { x: 168, title: "Measure", when: "end to Day 3", what: "reach is read" },
  { x: 312, title: "Review", when: "Day 4 to Day 8", what: "result is public", put: true },
  { x: 456, title: "Settle", when: "Day 5 to Day 10", what: "escrow pays out" },
  { x: 600, title: "Claim", when: "any time after", what: "never expires" },
];

/**
 * A campaign's phases in order, with the review put forward, because a result is public and
 * reviewed before any money moves. The mark on the line is Day 10, when a campaign that is still
 * not settled lets its brand take back the whole budget.
 */
export function CampaignTimeline() {
  return (
    <figure className={`p-4 sm:p-6 ${glass}`}>
      <div className="overflow-x-auto">
        <svg
          viewBox="0 52 760 172"
          role="img"
          aria-label="A campaign runs from its start to its end. Reach is read by Day 3. The result is public for review from Day 4 to Day 8. The escrow settles between Day 5 and Day 10. Claims are open at any time after. On Day 10 with no settlement, the brand takes back the whole budget."
          className="h-auto w-full min-w-[40rem]"
          fontSize="13"
        >
          <ArrowHead id="docs-timeline-arrow" />
          <g textAnchor="middle">
            {phases.map((phase) => (
              <g key={phase.title}>
                <rect
                  x={phase.x}
                  y="64"
                  width="136"
                  height="80"
                  rx="8"
                  className={phase.put ? main : box}
                />
                <text x={phase.x + 68} y="90" fontWeight="600" className={name}>
                  {phase.title}
                </text>
                <text x={phase.x + 68} y="110" fontSize="11.5" className={quiet}>
                  {phase.when}
                </text>
                <text x={phase.x + 68} y="128" fontSize="11.5" className={name}>
                  {phase.what}
                </text>
              </g>
            ))}
          </g>
          <path d="M24 168H736" className={line} markerEnd="url(#docs-timeline-arrow)" />
          <path d="M596 160l8 8l-8 8l-8 -8z" className="fill-brand-600 dark:fill-brand-400" />
          <g textAnchor="middle" fontSize="11.5">
            <text x="596" y="196" fontWeight="600" className={name}>
              Day 10 with no settlement
            </text>
            <text x="596" y="212" className={quiet}>
              the brand takes back the whole budget
            </text>
          </g>
        </svg>
      </div>
      <figcaption className={caption}>
        A result is public and reviewed before any money moves. The phases are not drawn to scale.
        Morcast settles only once the review has closed, and the contract accepts no settlement
        after Day 10.
      </figcaption>
    </figure>
  );
}
